<script setup>
import { ref } from 'vue';
import { Head, usePage } from '@inertiajs/vue3';
import { useStorage } from '@vueuse/core';
import { FilterMatchMode } from '@primevue/core/api';
import Button from 'primevue/button';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import { useToast } from 'primevue/usetoast';
import AuthenticatedAdminLayout from '@/Layouts/AuthenticatedAdminLayout.vue';

const page = usePage();

const toast = useToast();

let minPickupDate = null;
let maxPickupDate = null;

const dt = ref(null);

const orders = ref(
    [ ...page.props.orders ].map(
        order => {
          const dobDate = order.dob ? new Date(order.dob) : null;
          const orderDate = order.order_date ? new Date(order.order_date) : null;
          const pickupDate = order.pickup_date ? new Date(order.pickup_date) : null;

          if (pickupDate) {
            if (!minPickupDate || (pickupDate < minPickupDate)) {
              if (!minPickupDate) {
                minPickupDate = new Date();
              }

              minPickupDate.setTime(pickupDate.getTime());
            }

            if (!maxPickupDate || (pickupDate > maxPickupDate)) {
              if (!maxPickupDate) {
                maxPickupDate = new Date();
              }

              maxPickupDate.setTime(pickupDate.getTime());
            }
          }

          return {
            ...order,
            dob: dobDate,
            order_date: orderDate,
            pickup_date: pickupDate,
          };
        }
    )
);

const DEFAULT_FILTERS = {
  reference: { value: null, matchMode: FilterMatchMode.CONTAINS },
  order_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
  email: { value: null, matchMode: FilterMatchMode.CONTAINS },
  name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  dob: { value: null, matchMode: FilterMatchMode.DATE_IS },
  phone: { value: null, matchMode: FilterMatchMode.CONTAINS },
  address: { value: null, matchMode: FilterMatchMode.CONTAINS },
  postcode: { value: null, matchMode: FilterMatchMode.CONTAINS },
  city: { value: null, matchMode: FilterMatchMode.CONTAINS },
  quantity: { value: null, matchMode: FilterMatchMode.EQUALS },
  total_amount: { value: null, matchMode: FilterMatchMode.EQUALS },
  status: { value: null, matchMode: FilterMatchMode.EQUALS },
  pickup_date: { value: null, matchMode: FilterMatchMode.DATE_IS },
  pickup_hour: { value: null, matchMode: FilterMatchMode.EQUALS },
};

// The data table component does not have an equivalent for "mergeDefaults", and crashes when new filters are added.
const filters = useStorage(
    'order-list-filters',
    DEFAULT_FILTERS,
    sessionStorage,
    { mergeDefaults: true }
)

const resetFilters = () => {
  filters.value = { ...DEFAULT_FILTERS };
};

const STATUS_LABELS = {
  pending: 'En attente de paiement',
  canceled: 'Annulée',
  processing: 'Payée',
  completed: 'Terminée',
  pending_refund: 'À rembourser',
  refunded: 'Remboursée',
};

const STATUS_SEVERITIES = {
  canceled: 'danger',
  completed: 'secondary',
  pending: 'info',
  pending_refund: 'warn',
  processing: 'primary',
  refunded: 'contrast',
};

const currencyFormatter = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' });

const exportData = ({ data, field }) => {
  if (data instanceof Date) {
    const isDateOnly = [ 'dob', 'pickup_date' ].includes(field);

    return data.toLocaleDateString() + (isDateOnly ? '' : ' ' + data.toLocaleTimeString());
  } else if (field === 'total_amount') {
    return currencyFormatter.format(data);
  } else if (field === 'status') {
    return STATUS_LABELS[data];
  }

  return data;
};
</script>

<template>
  <AuthenticatedAdminLayout>
    <Head title="Liste des commandes"/>

    <main class="relative p-4 sm:px-6 lg:px-8 flex flex-col justify-center sm:items-center mx-auto size-full">
      <div class="flex flex-row justify-between items-center pb-4 size-full">
        <h1 class="text-xl sm:text-2xl text-primary uppercase font-semibold">
          Liste des commandes
        </h1>

        <div class="flex flex-row items-center gap-4">
          <Button
              label="Réinitialiser les filtres"
              icon="pi pi-filter-slash"
              severity="secondary"
              @click="resetFilters"
          />

          <Button
              label="Tout exporter"
              icon="pi pi-export"
              size="small"
              @click="dt?.exportCSV()"
          />
        </div>
      </div>

      <DataTable
          ref="dt"
          :value="orders"
          dataKey="id"
          v-model:filters="filters"
          :exportFunction="exportData"
          filterDisplay="row"
          resizableColumns
          columnResizeMode="expand"
          paginator
          :pageLinkSize="10"
          :rows="20"
          :rowsPerPageOptions="[10, 20, 50, 100]"
          removableSort
          scrollable
          scrollHeight="calc(100vh - 15rem)"
          size="large"
          showGridlines
          stripedRows
          style="width: 100%;"
      >
        <Column field="reference" header="N°" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="order_date" dataType="date" header="Date commande" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
                id="order_date_filter"
                fluid
                v-model="filterModel.value"
                dateFormat="dd/mm/yy"
                @update:modelValue="filterCallback"
            />
          </template>

          <template #body="{ data }">
            <span v-if="data.order_date">
              {{ data.order_date.toLocaleDateString() }}
              {{ data.order_date.toLocaleTimeString() }}
            </span>
          </template>
        </Column>

        <Column field="email" header="E-mail" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="name" header="Nom" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="dob" dataType="date" header="Date de naissance" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
                id="dob_filter"
                fluid
                v-model="filterModel.value"
                dateFormat="dd/mm/yy"
                @update:modelValue="filterCallback"
            />
          </template>

          <template #body="{ data }">
            <span v-if="data.dob">
              {{ data.dob.toLocaleDateString() }}
            </span>
          </template>
        </Column>

        <Column field="phone" header="Téléphone" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="address" header="Adresse" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="postcode" header="Code postal" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="city" header="Ville" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="quantity" dataType="numeric" header="Qté" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <Column field="total_amount" dataType="numeric" header="Total" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>

          <template #body="{ data }">
            <span v-if="data.total_amount">
              {{ currencyFormatter.format(data.total_amount) }}
            </span>
          </template>
        </Column>

        <Column field="status" header="Statut" :showFilterMenu="false" :showClearButton="false" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <Select
                v-model="filterModel.value"
                :options="Object.keys(STATUS_LABELS)"
                :optionLabel="option => STATUS_LABELS[option]"
                placeholder="Statut"
                @change="filterCallback()"
                :showClear="true"
            >
              <template #option="{ option }">
                <Tag :value="STATUS_LABELS[option]" :severity="STATUS_SEVERITIES[option]"/>
              </template>
            </Select>
          </template>

          <template #body="{ data }">
            <Tag :value="STATUS_LABELS[data.status]" :severity="STATUS_SEVERITIES[data.status]"/>
          </template>
        </Column>

        <Column field="pickup_date" dataType="date" header="Date retrait" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <DatePicker
                id="pickup_date_filter"
                fluid
                v-model="filterModel.value"
                dateFormat="dd/mm/yy"
                :minDate="minPickupDate"
                :maxDate="maxPickupDate"
                @update:modelValue="filterCallback"
            />
          </template>

          <template #body="{ data }">
            <span v-if="data.pickup_date">
              {{ data.pickup_date.toLocaleDateString() }}
            </span>
          </template>
        </Column>

        <Column field="pickup_hour" dataType="numeric" header="Heure retrait" sortable>
          <template #filter="{ filterModel, filterCallback }">
            <InputText v-model="filterModel.value" type="text" @input="filterCallback()" placeholder=""/>
          </template>
        </Column>

        <template #empty>
          <div class="my-4 font-bold text-center">Aucune commande trouvée.</div>
        </template>
      </DataTable>

      <Toast/>
    </main>
  </AuthenticatedAdminLayout>
</template>
